import React, { Dispatch, SetStateAction } from 'react'

import { Box, Rating, Typography, useTheme } from '@hermes/web-components'
import useTranslateMessage from '@hooks/useTranslateMessage'

import { ExtendedBook, ShippingPrice } from '@hermes/types'
import { useLocale } from '@hooks/useLocale'
import { navigationPaths } from '@hooks/useNavigation'
import { AuthorLink, HeaderContainer } from './ActionSection.styles'

import MainActions from './MainActions'
import SecondarySection from './SecondaryActions'

interface ActionSectionProps {
  setVersionsModalShow: Dispatch<SetStateAction<boolean>>
  navigationRefs: { [key: string]: React.RefObject<HTMLDivElement> }
  bookDetails: ExtendedBook
  shippingPrice: ShippingPrice
}

const ActionSection = ({ setVersionsModalShow, navigationRefs, bookDetails, shippingPrice }: ActionSectionProps) => {
  const { t } = useTranslateMessage()
  const theme = useTheme()
  const locale = useLocale()
  return (
    <HeaderContainer>
      <Box mb="36px" display="flex" flexDirection={'column'}>
        <Typography fontSize="14px" lineHeight="24px" width="100%" mb="4px">
          {`${t({ id: bookDetails.format })} • ${bookDetails.year}`}
        </Typography>

        <Typography variant="h2" mb="4px" lineHeight="36px">
          {bookDetails.title}
          {bookDetails.subtitle && `: ${bookDetails.subtitle}`}
        </Typography>

        {Boolean(bookDetails.authors.length) && (
          <Box display="flex" gap="5px" mb="12px" flexWrap="wrap">
            <Typography fontSize="16px" lineHeight="24px" color={theme.palette.neutral.main}>
              {t({ id: 'authors.by' })}
            </Typography>
            {bookDetails.authors.map((author, index) => (
              <AuthorLink
                href={`/${locale}/${navigationPaths.searchPage(author.fullName)}`}
                key={index}
                passHref
                prefetch
              >
                {`${author.fullName || author.fullName}${index + 1 !== bookDetails.authors.length ? ', ' : ''}`}
              </AuthorLink>
            ))}
          </Box>
        )}
        <Box display="inline-flex" gap="8px">
          <Rating readOnly value={bookDetails?.rating?.avgRating ?? 0} />
          <Typography fontSize="14px" lineHeight={1} paddingTop="1px">
            {t(
              { id: 'bdp.rating' },
              {
                amount: bookDetails?.rating?.votes ?? 0,
                max: 5,
                value: bookDetails?.rating?.avgRating.toFixed(1) ?? 0
              }
            )}
          </Typography>
        </Box>
      </Box>
      <MainActions
        bookDetails={bookDetails}
        shippingPrice={shippingPrice}
        setVersionsModalShow={setVersionsModalShow}
      />
      <SecondarySection bookDetails={bookDetails} navigationRefs={navigationRefs} />
    </HeaderContainer>
  )
}

export default ActionSection
